import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { type LifecycleEvent } from "@/repositories/interfaces";
import { repositoryFactory } from "@/repositories/RepositoryFactory";

export const useLifecycleEvents = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const lifecycleEventRepo = repositoryFactory.getLifecycleEventRepository();
  const queryKey = ["lifecycleEvents", user?.id];

  const {
    data: lifecycleEvents = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => lifecycleEventRepo.getAll(),
    enabled: !!user,
  });

  const createLifecycleEvent = useMutation({
    mutationKey: ["createLifecycleEvent"],
    mutationFn: (event: Omit<LifecycleEvent, "id" | "created_at">) =>
      lifecycleEventRepo.create(event),
    onMutate: async (newItem) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<LifecycleEvent[]>(queryKey);
      queryClient.setQueryData<LifecycleEvent[]>(queryKey, (old = []) => [
        {
          ...newItem,
          id: crypto.randomUUID(),
          created_at: new Date().toISOString(),
        } as LifecycleEvent,
        ...old,
      ]);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateLifecycleEvent = useMutation({
    mutationKey: ["updateLifecycleEvent"],
    mutationFn: ({
      id,
      ...updates
    }: Partial<LifecycleEvent> & { id: string }) =>
      lifecycleEventRepo.update(id, updates),
    onMutate: async ({ id, ...updates }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<LifecycleEvent[]>(queryKey);
      queryClient.setQueryData<LifecycleEvent[]>(queryKey, (old = []) =>
        old.map((evt) => (evt.id === id ? { ...evt, ...updates } : evt)),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteLifecycleEvent = useMutation({
    mutationKey: ["deleteLifecycleEvent"],
    mutationFn: (id: string) => lifecycleEventRepo.delete(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<LifecycleEvent[]>(queryKey);
      queryClient.setQueryData<LifecycleEvent[]>(queryKey, (old = []) =>
        old.filter((evt) => evt.id !== id),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const getEventsBySubscription = (subscriptionId: string) => {
    return lifecycleEvents.filter(
      (event) => event.subscription_id === subscriptionId,
    );
  };

  return {
    lifecycleEvents,
    isLoading,
    error,
    createLifecycleEvent,
    updateLifecycleEvent,
    deleteLifecycleEvent,
    getEventsBySubscription,
  };
};
